import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SkipSelf,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { EventModel, isBespokeScoreboard, isBespokeSport } from '@frontend/sports/betting-offer/feature/model';
import { ShortNameModule, ShortTeamNamesService } from '@frontend/sports/betting-offer/feature/short-team-names';
import { ScoreboardBackgroundConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ScrollFreezeService } from '@frontend/sports/common/core/feature/scroll-freeze';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TickerInterval, TickerService, TimerService } from '@frontend/sports/common/core/utils/timer';
import { WindowEventService } from '@frontend/sports/common/core/utils/window-event';
import { ModalDialogService } from '@frontend/sports/modal/feature';
import { DeviceService, HtmlNode, MediaQueryService, WINDOW } from '@frontend/vanilla/core';
import { Subscription, defer, distinctUntilChanged, map, skip } from 'rxjs';

import { AnimationComponentLoaderService, EventAnimationBinding } from '../../animation/animation-component-loader.service';
import { EpcotConfigService } from '../../common/epcot-config.service';
import { ComponentProxy } from '../../deferred/component-loader.service';
import { ColumnLayoutService } from '../../layout/column-layout.service';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { EventVideoStreamBinding, VideoStreamComponentLoaderService } from '../../video-stream/video-stream-component-loader.service';
import { showStreamingMessage } from '../../video-stream/video-stream.models';
import { DispatcherEvent, FeatureTab, HeaderContentDensity } from '../event-details-common.models';
import { EventDetailsHelperService } from '../event-details-helper.service';
import { HeaderNavigationService } from '../header-navigation/header-navigation.service';
import { ScoreboardComponent } from '../scoreboard/scoreboard.component';

@Component({
    selector: 'ms-header-content',
    templateUrl: 'header-content.component.html',
    standalone: false,
})
export class HeaderContentComponent implements OnChanges, OnDestroy, AfterViewInit {
    @Input() event: EventModel;
    @Input() feature?: TabBarItem;
    @Input() isShowcase = false;
    @Input() live: boolean;
    @Input() isNewEDPScoreboard = false;

    featureType = FeatureTab;
    size: HeaderContentDensity;
    sizeDensity = HeaderContentDensity;
    sportConstant = SportConstant;
    disableAnimation = true;
    eventAnimation: boolean;
    animationComponent: ComponentProxy<EventAnimationBinding>;
    hasAnimation: boolean;
    animationSubscription: Subscription;
    videoStreamComponent: ComponentProxy<EventVideoStreamBinding>;

    messagesExpanded = false;

    isAlertEnabled: boolean;
    isBespokeScoreboard: boolean;
    isExpanded = false;
    showStreamingMessage: boolean;

    scoreboardHeight?: number;
    bgImageUrl?: string;
    bgMaxHeight: number | undefined;

    private scrollThreshold = 75;
    private scrollUpThreshold = 35;
    private scrollPosition = 0;
    private scoreboardExpandedOffset = 0;
    private isAnimationInProgress = false;
    private originalSize: HeaderContentDensity;

    @HostBinding('style.height.px') height?: number;
    @HostBinding('style.max-height.px') maxHeight?: number;
    @HostBinding('style.overflow-y') scroll?: string;
    @HostBinding('style.background-image') bgDefaultImage?: string;
    @HostBinding('style.background-color') bgColor?: string;
    @ViewChild(ScoreboardComponent, { read: ElementRef }) scoreboard?: ElementRef<HTMLElement>;

    @Output() toggle = new EventEmitter<HeaderContentDensity>();
    @Output() videoPinToggle = new EventEmitter<boolean>();

    @HostBinding()
    get class(): string {
        return ['header-content', ...this.backgroundStyle].join(' ');
    }

    get element(): HTMLElement {
        return this.elementRef.nativeElement;
    }

    isVideoPinned = false;

    private backgroundStyle: string[] = [];
    private scrollSubscription?: Subscription;
    private orientationSubscription?: Subscription;
    private resizeSubscription: Subscription;
    private readonly destroyRef = inject(DestroyRef);

    eventStatisticsComponent$ = defer(() => import('../../statistics/event-statistics.component')).pipe(map((c) => c.EventStatisticsComponent));

    eventStatisticsComponentOutputs = {
        toggle: (expanded: unknown) => this.statisticsToggled(!!expanded),
    };

    private readonly _window = inject(WINDOW);

    constructor(
        private scrollContainer: ScrollContainerService,
        private windowEvent: WindowEventService,
        private elementRef: ElementRef,
        private htmlNode: HtmlNode,
        private scrollfreeze: ScrollFreezeService,
        private dispatcher: DispatcherService,
        private device: DeviceService,
        private ticker: TickerService,
        private timer: TimerService,
        private modalDialogService: ModalDialogService,
        private eventDetailsHelper: EventDetailsHelperService,
        private eventMediaHelperService: EventMediaHelperService,
        private columnLayoutService: ColumnLayoutService,
        private scoreboardBackgrounds: ScoreboardBackgroundConfig,
        private mediaService: MediaQueryService,
        private shortTeamNamesService: ShortTeamNamesService,
        private epcotConfig: EpcotConfigService,
        private headerNavigation: HeaderNavigationService,
        @SkipSelf() private parentChangeDetectorRef: ChangeDetectorRef,
        animationLoader: AnimationComponentLoaderService,
        videoLoader: VideoStreamComponentLoaderService,
    ) {
        this.animationComponent = animationLoader.getAnimationComponent();
        this.videoStreamComponent = videoLoader.getVideoStreamComponent();
        this.videoStreamComponent.subscribeToOutputs({ videoPinToggle: this.onVideoPinToggle });
    }

    ngAfterViewInit(): void {
        this.startListener();
        this.mediaService
            .observe()
            .pipe(
                map(() => this.mediaService.isActive('gt-md')),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.setBackGroundImage();
            });
    }

    ngOnChanges(changes: ISimpleChanges<HeaderContentComponent>): void {
        const featureChanged = changes.feature;
        const eventChanged = changes.event;
        const stageChanged = changes.live;

        this.isBespokeScoreboard = isBespokeScoreboard(this.event);
        this.showStreamingMessage = showStreamingMessage(this.event) && this.device.currentOrientation === 'portrait';

        if (eventChanged || featureChanged) {
            this.setBackGroundImage();

            if (eventChanged) {
                this.animationComponent.update({ event: this.event, scoreboard: this.event?.scoreboard });
                this.videoStreamComponent.update({ event: this.event, trackingSource: 'Showcase Play Button' });

                this.setAnimation();
            } else if (featureChanged) {
                this.disableAnimation = true;
            }

            this.setDensity();

            this.setBackgroundStyle();
            this.setShortNames();
        }

        if (featureChanged) {
            this.setFullscreen(this.isExpanded && this.feature?.id === this.featureType.Statistics);
        }

        if (!eventChanged && stageChanged) {
            if (this.size !== HeaderContentDensity.Tiny) {
                this.setDensity();
            }
            this.setBackgroundStyle();
        }
    }

    ngOnDestroy(): void {
        this.stopListener();

        if (this.scrollfreeze.isScrollFrozen) {
            this.scrollfreeze.releaseScroll();
        }

        this.animationSubscription?.unsubscribe();
        this.resizeSubscription?.unsubscribe();
    }

    @HostListener('transitionend')
    transitionEnded(): void {
        if (this.columnLayoutService.layout.isRightVisible) {
            return;
        }

        if (!this.maxHeight) {
            this.toggle.emit(this.size);
            this.resetFullscreen();
        }

        this.startListener();
    }

    @HostListener('click')
    clicked(): void {
        if (this.event.live && (this.size === HeaderContentDensity.Tiny || this.size === HeaderContentDensity.Small)) {
            this.setDensity();
            this.setBackgroundStyle();
            this.scrollContainer.scrollTo(0, 0);
        }
    }

    messageToggled(expanded: boolean): void {
        this.setFullscreen(expanded, false, () => this.messageFullscreen());
        this.isExpanded = expanded;
        this.setBackGroundImage();
    }

    statisticsToggled(expanded: boolean): void {
        if (!this.isShowcase) {
            this.scrollContainer.scrollTo(0, 0);
            this.timer.setTimeout(() => {
                this.setFullscreen(expanded, true);
                this.parentChangeDetectorRef.detectChanges();
            });
        } else if (this.isExpanded) {
            this.scrollContainer.scrollTo(0, 0);
        }
        this.isExpanded = expanded;
    }

    alertEnabled(): void {
        this.isAlertEnabled = true;
    }

    onStreamMessageClick(): void {
        this.headerNavigation.goByTab(FeatureTab.Video);
    }

    private setDensity(): void {
        if (this.eventAnimation) {
            this.scoreboardExpandedOffset = 170;
            this.size = HeaderContentDensity.Medium;
        } else if (this.event.live && this.event.scoreboard.started) {
            this.scoreboardExpandedOffset = 100;
            this.size = HeaderContentDensity.Medium;
        } else {
            this.scoreboardExpandedOffset = 100;
            this.size = HeaderContentDensity.Small;
        }
        this.parentChangeDetectorRef.markForCheck();
    }

    private getBackground(): string | undefined {
        return this.eventDetailsHelper.getBackgroundColor(
            this.event,
            this.eventAnimation &&
                this.size !== HeaderContentDensity.Tiny &&
                (this.feature?.id === FeatureTab.Scoreboard || this.feature?.id === FeatureTab.Simulation),
        );
    }

    private setAnimation(): void {
        this.checkAnimation();

        if (!this.event.live) {
            return;
        }

        if (!this.hasAnimation) {
            return;
        }

        this.animationSubscription?.unsubscribe();

        this.animationSubscription = this.ticker.subscribe(() => {
            this.checkAnimation();

            if (this.eventAnimation) {
                this.animationSubscription.unsubscribe();
            }
        }, TickerInterval.Second);

        this.resizeSubscription = this.windowEvent.resize.subscribe(() => this.checkAnimation());
    }

    private checkAnimation(): void {
        this.hasAnimation =
            !this.columnLayoutService.layout.isRightVisible &&
            this.eventMediaHelperService.hasAnimation(this.event) &&
            !this.eventMediaHelperService.hasBetradarAnimation(this.event);

        const currentAnimation = this.hasAnimation;

        if (currentAnimation !== this.eventAnimation) {
            this.eventAnimation = currentAnimation;
            this.setDensity();
            this.setBackgroundStyle();
        }
    }

    private setBackgroundStyle(): void {
        const backgroundColor = this.getBackground();

        const backgroundStyle = [];

        if (backgroundColor) {
            backgroundStyle.push(backgroundColor);
        }

        if (this.messagesExpanded) {
            backgroundStyle.push('expanded');
            backgroundStyle.push('disable-animation');
            this.timer.setTimeout(() => (this.disableAnimation = false));
        }

        if (this.feature?.id === FeatureTab.Scoreboard) {
            if (this.disableAnimation) {
                backgroundStyle.push('disable-animation');
                this.timer.setTimeout(() => (this.disableAnimation = false));
            }
        }

        if (this.epcotConfig.isEnabled()) {
            backgroundStyle.push('fade-in');
        }

        this.backgroundStyle = backgroundStyle;
    }

    private startListener(): void {
        if (!this.orientationSubscription) {
            // we skip the first (it is a behaviour subject) as we do not care about the first one
            this.orientationSubscription = this.device.orientation.pipe(skip(1)).subscribe(() => {
                if (this.maxHeight) {
                    this.timer.setTimeout(() => this.setHeight());
                    if (this.device.currentOrientation !== 'portrait') {
                        this.scrollfreeze.forceScroll(0, 0);
                    }
                }

                this.setBackgroundDensity();
            });
        }

        if (!this.scrollSubscription) {
            this.scrollSubscription = this.scrollContainer.scroll.subscribe(() => {
                const isModalDialogOpen = this.scrollfreeze.isScrollFrozen && this.modalDialogService.isModalDialogOpen();
                if (!this.htmlNode.hasCssClass('mainmenu_on') && !isModalDialogOpen) {
                    this.setBackgroundDensity();
                }
            });
        }
    }

    private stopListener(options: { skipScroll?: boolean; skipOrientation?: boolean } = {}): void {
        if (this.orientationSubscription && !options.skipOrientation) {
            this.orientationSubscription.unsubscribe();
            this.orientationSubscription = undefined;
        }

        if (this.scrollSubscription && !options.skipScroll) {
            this.scrollSubscription.unsubscribe();
            this.scrollSubscription = undefined;
        }
    }

    private setFullscreen(expanded: boolean = false, scrollable: boolean = false, expandedCallback: () => void = () => void 0): void {
        if (expanded) {
            if (this.columnLayoutService.layout.isRightVisible) {
                expandedCallback();
                this.messagesExpanded = true;
            } else {
                this.setHeight();
                expandedCallback();

                if (scrollable) {
                    this.scroll = 'scroll';
                }

                this.scrollfreeze.freezeScroll();
            }

            this.stopListener({ skipOrientation: true });
        } else {
            this.maxHeight = undefined;
            this.height = undefined;
            this.messagesExpanded = false;

            if (scrollable) {
                this.scroll = undefined;
                this.element.scroll({ top: 0, left: 0 });
            }

            if (this.scrollfreeze.isScrollFrozen && !this.modalDialogService.isModalDialogOpen()) {
                this.scrollfreeze.releaseScroll();
            }

            this.startListener();
        }

        this.isExpanded = expanded;
        this.setBackgroundStyle();
        this.dispatcher.dispatch(DispatcherEvent.EventDetails.Expanded, expanded);
    }

    private resetFullscreen(): void {
        this.height = undefined;
        this.scoreboardHeight = undefined;
    }

    private messageFullscreen(): void {
        this.scoreboardHeight = this.scoreboard?.nativeElement.offsetHeight;
    }

    private setHeight(): void {
        const container = this.element.getBoundingClientRect();
        this.maxHeight = this.height = this._window.innerHeight - container.top;
        this.bgMaxHeight = container.height;
    }

    private setBackgroundDensity(): void {
        if (this.isAnimationInProgress && this.isNotScrolledTop()) {
            this.scrollPosition = this.scrollContainer.scrollTop;

            return;
        }

        if (
            this.device.currentOrientation !== 'portrait' ||
            (this.feature && this.feature.id !== FeatureTab.Scoreboard && this.feature.id !== FeatureTab.Simulation)
        ) {
            this.setDensity();
        }

        if (this.isExpanded) {
            return;
        }

        if (
            this.device.currentOrientation === 'portrait' &&
            this.scrollContainer.scrollTop > this.scrollThreshold &&
            this.scrollContainer.scrollTop > this.scrollPosition + this.scoreboardExpandedOffset
        ) {
            this.originalSize = this.size;
            if (this.isBespokeScoreboard) {
                this.size = HeaderContentDensity.Tiny;
            } else {
                this.size = this.event.scoreboard.started || this.eventAnimation ? HeaderContentDensity.Tiny : HeaderContentDensity.Small;
            }

            if (this.originalSize !== this.size) {
                this.isAnimationInProgress = true;
                this.timer.setTimeout(() => (this.isAnimationInProgress = false), 1000);
                this.parentChangeDetectorRef.markForCheck();
            }
        } else if (
            this.device.currentOrientation === 'portrait' &&
            this.scrollContainer.scrollTop <= this.scrollUpThreshold &&
            this.scrollContainer.scrollTop <= this.scrollPosition + this.scoreboardExpandedOffset
        ) {
            this.originalSize = this.size;
            this.setDensity();

            if (this.originalSize !== this.size) {
                this.isAnimationInProgress = true;
                this.timer.setTimeout(() => (this.isAnimationInProgress = false), 500);
            }
        }

        this.scrollPosition = this.scrollContainer.scrollTop;
        this.scoreboardExpandedOffset = 0;

        this.setBackgroundStyle();
        this.setAnimationSticky();
    }

    private setAnimationSticky(): void {
        if (this.feature?.id === FeatureTab.Simulation && !this.isShowcase) {
            this.animationComponent.update({ showSticky: this.size === HeaderContentDensity.Tiny });
        }
    }

    private isNotScrolledTop(): boolean {
        return !(this.scrollPosition !== 0 && this.scrollContainer.scrollTop <= 0);
    }

    private onVideoPinToggle = (isVideoPinned: boolean): void => {
        this.isVideoPinned = isVideoPinned;
        this.videoPinToggle.emit(isVideoPinned);
    };

    private setShortNames(): void {
        const event = this.event;
        if (
            event.participants.length &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[0], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('01', 0);
        }

        if (
            event.participants.length > 1 &&
            this.shortTeamNamesService.shouldUseShortName(event.participants[1], event.sport.id, event.league.id, ShortNameModule.Grids)
        ) {
            this.setShortName('02', 1);
        }
    }

    private setShortName(player: string, index: number): void {
        const scoreboard = <any>this.event.scoreboard;
        const participants = this.event.participants;
        const short = participants[index].shortName;

        if (scoreboard.playerInfo) {
            scoreboard.playerInfo.players[player].teamName = short;
        }

        if (scoreboard.score?.playerInfo) {
            scoreboard.score.playerInfo.players[player].teamName = short;
        }
    }

    private setBackGroundImage = (): void => {
        const largeScreen = this.mediaService.isActive('gt-md');
        const mediumScreen = this.mediaService.isActive('md');
        const smallScreen = this.mediaService.isActive('lt-md');
        const background = this.scoreboardBackgrounds.backgrounds.find((x) => x.sportId === this.event.sport.id);
        const isBeSpokeSport = isBespokeSport(this.event.sport.id, this.event.sport.isEsport);

        if (isBeSpokeSport && background && this.feature) {
            if (this.feature.id === FeatureTab.Scoreboard && (largeScreen || !this.isExpanded)) {
                if (background.src) {
                    this.bgDefaultImage = undefined;
                    this.bgImageUrl = background.src;
                    this.bgColor = background.outerColor;
                    this.bgMaxHeight = undefined;
                }
            } else if (this.feature.id === FeatureTab.Statistics || this.isExpanded) {
                if (smallScreen) {
                    this.bgDefaultImage = `radial-gradient(circle 188px at top, ${background.innerColor}, ${background.outerColor})`;
                } else if (mediumScreen) {
                    this.bgDefaultImage = `radial-gradient(229px 72px at top center, ${background.innerColor}, ${background.outerColor})`;
                }
                this.bgColor = undefined;
            } else if (this.feature.id === FeatureTab.Video) {
                this.bgColor = background.outerColor;
                this.bgDefaultImage = undefined;
            } else if (this.feature.id === FeatureTab.Simulation) {
                this.bgDefaultImage = `radial-gradient(circle 188px at top, ${background.innerColor}, ${background.outerColor})`;
                this.bgColor = undefined;
            }
        } else {
            this.bgDefaultImage = undefined;
            this.bgColor = undefined;
        }
    };
}
